import React, { useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { motion, useReducedMotion, useTransform } from "framer-motion";

import { breakpoints, colors, sectionRhythm } from "../../../theme";
import useTitleToTopProgress from "../../hooks/use-section-scroll-progress";
import useMediaQuery from "../../hooks/use-media-query";
import {
  HERO_IMAGE_HEIGHT,
  HERO_IMAGE_WIDTH,
} from "../../containers/projects/project-page-media";

const HEADER_DESKTOP = sectionRhythm.headerHeight.desktop;

const MobileLayout = styled.div`
  width: 100%;
  margin-bottom: 1.5rem;

  h1 {
    margin: 0 0 16px !important;
    text-align: left !important;
  }
`;

const MobileMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const HelperText = styled.span`
  color: ${colors.grey.darkMedium};
`;

const MobileMedia = styled.div`
  width: 100%;
  margin-top: 1.5rem;

  & > * {
    display: block;
    width: 100%;
    height: auto;
    margin: 0 !important;
  }
`;

const Runway = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 94vw;
  margin-left: calc(50% - 47vw);
  padding-top: calc(25vh - ${HEADER_DESKTOP}px);
  padding-bottom: 2.5rem;
  margin-bottom: 2rem;

  @media only screen and (min-width: ${breakpoints.desktop}) {
    width: 85vw;
    margin-left: calc(50% - 42.5vw);
  }

  @media only screen and (min-width: 1260px) {
    width: 90vw;
    margin-left: calc(50% - 45vw);
  }
`;

const Stage = styled.div`
  position: relative;
  width: 100%;
  perspective: 1200px;
  transform-style: preserve-3d;
`;

const Header = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  margin: 0 auto;
  padding-bottom: 1rem;
  text-align: center;
  box-sizing: border-box;

  h1 {
    margin: 0 !important;
    text-align: center;
    font-size: 44px;

    @media only screen and (min-width: ${breakpoints.desktop}) {
      font-size: 60px;
    }
  }
`;

const Card = styled(motion.div)`
  position: relative;
  z-index: 0;
  width: 65vw;
  aspect-ratio: ${HERO_IMAGE_WIDTH} / ${HERO_IMAGE_HEIGHT};
  margin-left: calc(50% - 32.5vw);
  padding: 1.5rem;
  box-sizing: border-box;
  border: 4px solid #6c6c6c;
  border-radius: 30px;
  background: #222222;
  box-shadow: 0px 0px 30px 0px rgba(0, 0, 0, 0.3);
  transform-origin: center top;
  transform-style: preserve-3d;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    will-change: auto;
  }
`;

const CardInner = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 1rem;
  background: #f3f4f6;
  box-sizing: border-box;

  & > * {
    display: block;
    width: 100%;
    height: 100% !important;
    margin: 0 !important;
    aspect-ratio: auto !important;
    object-fit: cover;
    object-position: left top;
    border-radius: 1rem !important;
  }
`;

const TABLET_UP_QUERY = `(min-width: ${breakpoints.tablet})`;

const ContainerScroll = ({
  titleComponent,
  metaComponent,
  role,
  year,
  children,
}) => {
  const titleRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const isTabletUp = useMediaQuery(TABLET_UP_QUERY);
  const scrollYProgress = useTitleToTopProgress(titleRef);

  const rotate = useTransform(
    scrollYProgress,
    [0, 1],
    reduceMotion ? [0, 0] : [20, 0],
  );
  const scale = useTransform(
    scrollYProgress,
    [0, 1],
    reduceMotion ? [1, 1] : [1.05, 1],
  );

  if (!isTabletUp) {
    return (
      <MobileLayout>
        {titleComponent}
        {metaComponent ||
          (role || year ? (
            <MobileMeta>
              {role ? <HelperText>Role: {role}</HelperText> : null}
              {year ? <HelperText>Year: {year}</HelperText> : null}
            </MobileMeta>
          ) : null)}
        <MobileMedia>{children}</MobileMedia>
      </MobileLayout>
    );
  }

  return (
    <Runway>
      <Stage>
        {titleComponent ? (
          <Header ref={titleRef}>{titleComponent}</Header>
        ) : null}
        <Card style={{ rotateX: rotate, scale }}>
          <CardInner>{children}</CardInner>
        </Card>
      </Stage>
    </Runway>
  );
};

ContainerScroll.propTypes = {
  titleComponent: PropTypes.node,
  metaComponent: PropTypes.node,
  role: PropTypes.string,
  year: PropTypes.string,
  children: PropTypes.node,
};

export default ContainerScroll;
